import type { FeatureLike } from 'ol/Feature';
import Feature from 'ol/Feature';

import type { ServiceKey } from '@/utils/types/baseStationsTypes';
import { FEATURE_TYPE, FEATURE_TYPE_VALUES } from '@/components/map/consts/mapFeatureConsts';
import type {
	FeatureDataByType,
	FeatureType,
	TypedMapFeature,
} from '@/components/map/types/mapFeatureTypes';
import type {
	Baseline,
	BaseStationStyleFeatureData,
	ConnectedBaseStation,
	FeatureData,
} from '@/components/map/types/mapFeatureTypes';


export type FeatureId = number | string;


const FEATURE_TYPE_SET = new Set<string>(FEATURE_TYPE_VALUES);


export const isFeatureData = (value: unknown): value is FeatureData => (
	value != null && typeof value === 'object'
);


export const getFeatureType = (feature: FeatureLike): FeatureType | null => {
	const rawType = feature.get('type');
	if (typeof rawType !== 'string' || !FEATURE_TYPE_SET.has(rawType)) {
		return null;
	}
	return rawType as FeatureType;
};


// TODO(TS_MIGRATION): replace with typed Feature<Geometry> property accessors once map features are fully typed.
export const getFeatureData = (feature: FeatureLike): unknown => feature.get('featureData');


const getFeatureDataId = (featureData: unknown): FeatureId | null => {
	if (featureData == null || typeof featureData !== 'object') {
		return null;
	}
	const id = Reflect.get(featureData, 'id');
	if (typeof id === 'number' || typeof id === 'string') {
		return id;
	}
	return null;
};


// TODO(TS_MIGRATION): replace with typed Feature<Geometry> property accessors once map features are fully typed.
export const getFeatureId = (feature: FeatureLike): FeatureId | null => (
	getFeatureDataId(getFeatureData(feature))
);


export const isBaseStationStyleFeatureData = (value: unknown): value is BaseStationStyleFeatureData => {
	if (!isFeatureData(value)) {
		return false;
	}
	const key = Reflect.get(value, 'key');
	return typeof key === 'number';
};


export const isGetterActiveFeatureData = (
	value: unknown,
): value is FeatureData & { service: ServiceKey; connectedBaseStation: ConnectedBaseStation | null } => {
	if (!isFeatureData(value)) {
		return false;
	}
	const service = Reflect.get(value, 'service');
	if (typeof service !== 'string') {
		return false;
	}
	const connectedBaseStation = Reflect.get(value, 'connectedBaseStation');
	return connectedBaseStation === null
		|| (typeof connectedBaseStation === 'object' && connectedBaseStation !== null);
};


export const isGetterFailedFeatureData = (
	value: unknown,
): value is FeatureData & { service: ServiceKey; is_new: boolean } => {
	if (!isFeatureData(value)) {
		return false;
	}
	const service = Reflect.get(value, 'service');
	const isNew = Reflect.get(value, 'is_new');
	return typeof service === 'string' && typeof isNew === 'boolean';
};


export const isBaselineFeatureData = (value: unknown): value is Baseline & FeatureData => {
	if (!isFeatureData(value)) {
		return false;
	}
	const id = Reflect.get(value, 'id');
	const status = Reflect.get(value, 'status');
	return typeof id === 'number' && typeof status === 'string';
};


export const isCnetGridpointFeatureData = (
	value: unknown,
): value is FeatureData & { selected: boolean; gpIndex: number } => {
	if (!isFeatureData(value)) {
		return false;
	}
	const selected = Reflect.get(value, 'selected');
	const gpIndex = Reflect.get(value, 'gpIndex');
	return typeof selected === 'boolean' && typeof gpIndex === 'number';
};


export const isRoverHistoryFeatureData = (
	value: unknown,
): value is FeatureData & { quality: number } => {
	if (!isFeatureData(value)) {
		return false;
	}
	const quality = Reflect.get(value, 'quality');
	return typeof quality === 'number';
};


export const isQualityRectangleFeatureData = (
	value: unknown,
): value is FeatureData & { q_color_value: number } => {
	if (!isFeatureData(value)) {
		return false;
	}
	const qColorValue = Reflect.get(value, 'q_color_value');
	return typeof qColorValue === 'number';
};


const isGridpointFeatureData = (
	value: unknown,
): value is FeatureDataByType['gridpoint'] => {
	if (!isFeatureData(value)) {
		return false;
	}
	const id = Reflect.get(value, 'id');
	const label = Reflect.get(value, 'label');
	return typeof id === 'string' && typeof label === 'string';
};


const isCcmOtherGridpointFeatureData = (
	value: unknown,
): value is FeatureDataByType['cnetConstructorOtherGridpoint'] => {
	if (!isFeatureData(value)) {
		return false;
	}
	const label = Reflect.get(value, 'label');
	const color = Reflect.get(value, 'color');
	return typeof label === 'string' && typeof color === 'string';
};


export function isFeatureDataForType<T extends FeatureType>(
	data: unknown,
	type: T,
): data is FeatureDataByType[T] {
	if (!isFeatureData(data)) {
		return false;
	}
	switch (type) {
	case FEATURE_TYPE.baseStation:
	case FEATURE_TYPE.baseStationCircle:
	case FEATURE_TYPE.baseStationLabel:
	case FEATURE_TYPE.baseStationSubnetworkCircles:
	case FEATURE_TYPE.vrsStation:
	case FEATURE_TYPE.vrsStationCircle:
		return isBaseStationStyleFeatureData(data);
	case FEATURE_TYPE.getterActive:
	case FEATURE_TYPE.getterLink:
		return isGetterActiveFeatureData(data);
	case FEATURE_TYPE.getterFailed:
		return isGetterFailedFeatureData(data);
	case FEATURE_TYPE.baselineLinkSelfCircle:
	case FEATURE_TYPE.baselineLinkLine:
	case FEATURE_TYPE.baselineLinkArrowHead:
		return isBaselineFeatureData(data);
	case FEATURE_TYPE.cnetConstructorGridpoint:
		return isCnetGridpointFeatureData(data);
	case FEATURE_TYPE.cnetConstructorOtherGridpoint:
		return isCcmOtherGridpointFeatureData(data);
	case FEATURE_TYPE.roverHistoryCurrent:
	case FEATURE_TYPE.roverHistoryFragment:
	case FEATURE_TYPE.roverHistoryFragmentBreakEnd:
	case FEATURE_TYPE.roverHistoryFragmentNormalEnd:
	case FEATURE_TYPE.roverHistoryFragmentLastEnd:
		return isRoverHistoryFeatureData(data);
	case FEATURE_TYPE.qualityRectangle:
		return isQualityRectangleFeatureData(data);
	case FEATURE_TYPE.gridpoint:
		return isGridpointFeatureData(data);
	case FEATURE_TYPE.geoFencingCircle:
	case FEATURE_TYPE.profileGeoFencingCenter:
	case FEATURE_TYPE.profileGeoFencingCircle:
	case FEATURE_TYPE.coverageContour:
	case FEATURE_TYPE.anchorPoint:
		return true;
	default: {
		const exhaustiveCheck: never = type;
		return exhaustiveCheck === undefined;
	}
	}
}


export const asTypedMapFeature = (feature: FeatureLike): TypedMapFeature | null => {
	const type = getFeatureType(feature);
	if (type == null) {
		return null;
	}
	const data = getFeatureData(feature);
	if (!isFeatureDataForType(data, type)) {
		return null;
	}
	if (!(feature instanceof Feature)) {
		return null;
	}
	return {
		type,
		featureData: data,
		ol: feature,
	};
};


export const isTypedMapFeatureOfType = <T extends FeatureType>(
	typed: TypedMapFeature | null,
	type: T,
): typed is TypedMapFeature<T> => {
	return typed != null && typed.type === type;
};

// TODO(TS_MIGRATION): replace with typed featureData field access once featureData types are narrowed.
export const getBaselineId = (featureData: Baseline & FeatureData): number | null => (
	typeof featureData.id === 'number' ? featureData.id : null
);


export const getMountpointDisplay = (
	featureData: Pick<BaseStationStyleFeatureData, 'mountpointDisplay'>,
): string | null | undefined => featureData.mountpointDisplay;


export const getSubNetworkIds = (featureData: BaseStationStyleFeatureData): number[] => {
	const subNetworkIds = featureData.subNetworkIds;
	if (!Array.isArray(subNetworkIds)) {
		return [];
	}
	return subNetworkIds.filter((id): id is number => typeof id === 'number');
};
